import { z } from "zod";
import { isoDateSchema, usPhoneSchema } from "./common.js";

// =============================================================================
// Patient lookup — request + response shape for /api/patient/lookup.
//
// The request requires both MRN and DOB; the server hashes (salt + MRN + DOB)
// and looks up the resulting key in `patients`. On a miss the response is
// indistinguishable from a wrong-DOB hit, by design — we never want this
// endpoint to leak whether a particular MRN exists in our database.
// =============================================================================

export const mrnSchema = z
  .string()
  .min(1, "MRN is required")
  .max(50, "MRN is too long")
  .transform((v) => v.trim());

export const patientLookupRequestSchema = z.object({
  mrn: mrnSchema,
  dob: isoDateSchema,
});
export type PatientLookupRequest = z.infer<typeof patientLookupRequestSchema>;

// =============================================================================
// Saved-value entries — distilled from the patient's prior bookings so the
// form can show "use a saved place / phone / requester" pickers next to the
// matching fields. Each list is sorted most-recently-used first; the API
// caps each at a small N to keep responses tight.
// =============================================================================

export const savedPickupSchema = z.object({
  address: z.string(),
  cityZip: z.string().default(""),
  location: z.string().default(""),
  lat: z.number().nullable().default(null),
  lon: z.number().nullable().default(null),
  lastUsedAt: z.string(),
});
export type SavedPickup = z.infer<typeof savedPickupSchema>;

export const savedDestinationSchema = z.object({
  address: z.string(),
  cityZip: z.string().default(""),
  lat: z.number().nullable().default(null),
  lon: z.number().nullable().default(null),
  lastUsedAt: z.string(),
});
export type SavedDestination = z.infer<typeof savedDestinationSchema>;

export const savedPhoneSchema = z.object({
  phone: z.string(),
  lastUsedAt: z.string(),
});
export type SavedPhone = z.infer<typeof savedPhoneSchema>;

export const savedRequesterSchema = z.object({
  requestedBy: z.string(),
  callbackPhone: z.string().default(""),
  requesterEmail: z.string().default(""),
  lastUsedAt: z.string(),
});
export type SavedRequester = z.infer<typeof savedRequesterSchema>;

export const savedDepartmentSchema = z.object({
  department: z.string(),
  costCenter: z.string().default(""),
  lastUsedAt: z.string(),
});
export type SavedDepartment = z.infer<typeof savedDepartmentSchema>;

/** A trip already on the books for this patient — surfaced on patient
 *  lookup so the requester can spot duplicates before booking another.
 *  Two relevance tiers (computed client-side from `transportDate` +
 *  `createdAt`):
 *
 *  - High-signal (amber warning): trip is for **today**, or was
 *    created in the last 6 hours. Blocks "Yes, this is the right
 *    patient" until acknowledged.
 *  - Low-signal (slate info): future-dated trip, created more than
 *    6 hours ago — informational only.
 *
 *  `createdByUserId` lets the UI distinguish "you booked this" from
 *  "someone else did" — different copy, different tone. */
export const upcomingBookingSchema = z.object({
  friendlyId: z.string(),
  transportDate: z.string(),
  pickupTimeEstimated: z.string().default(""),
  appointmentTime: z.string().default(""),
  createdAt: z.string(),
  createdByUserId: z.string().nullable().default(null),
  createdByName: z.string().default(""),
  /** Live triplink status — drives the small status chip next to the
   *  trip ID in the duplicate-detection panel. Empty when the row
   *  hasn't been touched by the sync yet (rare; a freshly-submitted
   *  V2 booking the timer hasn't picked up). */
  status: z.string().default(""),
});
export type UpcomingBooking = z.infer<typeof upcomingBookingSchema>;

// =============================================================================
// Same-date duplicate detection
//
// Mount Sinai run ~a dozen departments booking into one portal, and none of
// them can see what another has already booked. In Aug 2026 the same patient
// was booked for the same day by two different departments a day apart; both
// round trips would have been dispatched and billed had it not been spotted by
// eye. Their previous vendor had the same failure mode, and avoiding it is an
// explicit reason they moved.
//
// The lookup panel already escalates two cases at patient-lookup time (a trip
// TODAY, or one booked in the last 6h). Neither could catch this one: the
// booker has not chosen a date yet at that point, so a trip several days out
// booked yesterday reads as ordinary history.
//
// This runs later, once a date IS known, and asks the only question that
// actually matters: does this patient already have a trip on the day I am
// about to book? It stays a WARNING rather than a block — a patient can
// legitimately need two trips in one day (morning dialysis, afternoon clinic),
// so the booker acknowledges and proceeds.
// =============================================================================

/** An existing trip that falls on a date the booker is about to book. */
export interface TripDateCollision {
  /** The date being booked, `YYYY-MM-DD`. */
  date: string;
  /** Uncancelled trips already on file for that date, in the order the
   *  lookup returned them (soonest first). */
  existing: UpcomingBooking[];
}

/** Normalize to a bare `YYYY-MM-DD`. Both sides come from a Postgres `date`
 *  column today, but a value that ever picks up a time component must still
 *  match by calendar day rather than silently failing to collide. */
function toDateKey(value: string | null | undefined): string {
  return String(value ?? "").trim().slice(0, 10);
}

/**
 * Which of `datesBeingBooked` already have a trip on file for this patient.
 *
 * Pure and side-effect free so it can be unit-tested without a database or a
 * rendered form — the UI decides how loudly to present the result.
 *
 * Handles the recurring case: a weekly series is passed as its full date list
 * and every colliding occurrence comes back, so the booker sees all of them
 * rather than only the first.
 *
 * Returns collisions ordered by date; dates with no existing trip are omitted,
 * so an empty array means "nothing to warn about".
 */
export function findTripDateCollisions(
  datesBeingBooked: readonly string[],
  upcoming: readonly UpcomingBooking[],
): TripDateCollision[] {
  const byDate = new Map<string, UpcomingBooking[]>();
  for (const b of upcoming) {
    const key = toDateKey(b.transportDate);
    if (!key) continue;
    const list = byDate.get(key);
    if (list) list.push(b);
    else byDate.set(key, [b]);
  }

  const seen = new Set<string>();
  const out: TripDateCollision[] = [];
  for (const raw of datesBeingBooked) {
    const key = toDateKey(raw);
    // Skip blanks (the field is empty mid-typing) and repeats (a recurring
    // list should never contain the same day twice, but a duplicate must not
    // produce a duplicate warning).
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const existing = byDate.get(key);
    if (existing && existing.length > 0) out.push({ date: key, existing });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

/** Fields we'll prefill on the form when a returning patient is found.
 *  These are the *defaults* — the form treats every field as overridable.
 *  In addition, the saved* arrays carry the patient's history so the UI can
 *  surface explicit "use a saved place / phone / requester" pickers. */
export const patientRecordSchema = z.object({
  firstName: z.string().min(1).max(50),
  lastName: z.string().min(1).max(50),
  dob: isoDateSchema,
  department: z.string().max(100).optional().default(""),
  costCenter: z.string().max(20).optional().default(""),
  /** Patient contact phone from the most recent successful booking — 10
   *  digits, no formatting. Empty when we've never captured one. */
  contactPhone: z.string().max(20).optional().default(""),
  bookingCount: z.number().int().nonnegative().optional().default(0),

  savedPickups: z.array(savedPickupSchema).default([]),
  savedDestinations: z.array(savedDestinationSchema).default([]),
  savedContactPhones: z.array(savedPhoneSchema).default([]),
  savedCallbackPhones: z.array(savedPhoneSchema).default([]),
  savedRequesters: z.array(savedRequesterSchema).default([]),
  savedDepartments: z.array(savedDepartmentSchema).default([]),
  /** Trips already booked for this patient that the requester should
   *  glance at before submitting another. See `upcomingBookingSchema`
   *  for the relevance rules. */
  upcomingBookings: z.array(upcomingBookingSchema).default([]),
});
export type PatientRecord = z.infer<typeof patientRecordSchema>;

export const patientLookupResponseSchema = z.discriminatedUnion("found", [
  z.object({ found: z.literal(true), patient: patientRecordSchema }),
  z.object({ found: z.literal(false) }),
]);
export type PatientLookupResponse = z.infer<typeof patientLookupResponseSchema>;

/** Internal — used by the API on every successful trip create to keep the
 *  patient table fresh. Not exposed as an HTTP route from the frontend. */
export const patientUpsertSchema = patientRecordSchema.extend({
  mrn: mrnSchema,
});
export type PatientUpsert = z.infer<typeof patientUpsertSchema>;

// =============================================================================
// Patient name correction — POST /api/patient/update-name
//
// Lets the user fix a patient's name spelling / casing across every
// booking row + the patients row (if one exists for the MRN). Used
// from two places:
//   * the trip-form's FoundPatientCard — "this is the patient but the
//     name's off" workflow during booking
//   * Admin → Members — bulk cleanup of TMS-imported casing/spelling
//
// We rely on the user knowing the right MRN to gate edits — there's
// no DOB verification step here because the caller has already
// confirmed the MRN via the patient lookup OR is an admin in the
// Members directory. Every update is audit-logged.
// =============================================================================

export const patientUpdateNameRequestSchema = z.object({
  mrn: mrnSchema,
  firstName: z.string().trim().min(1).max(50),
  lastName: z.string().trim().min(1).max(50),
});
export type PatientUpdateNameRequest = z.infer<
  typeof patientUpdateNameRequestSchema
>;

export const patientUpdateNameResponseSchema = z.object({
  /** How many `bookings` rows were updated. Mostly informational —
   *  the UI uses it to render a toast like "Renamed across 14 trips". */
  updatedBookings: z.number().int().nonnegative(),
  /** Whether the canonical `patients` row was also updated. Will be
   *  false when the patient only exists in synced bookings (no
   *  verified DOB → no patients row to update). */
  updatedPatient: z.boolean(),
});
export type PatientUpdateNameResponse = z.infer<
  typeof patientUpdateNameResponseSchema
>;

// =============================================================================
// /api/patient/update-mrn
//
// Change the MRN on every booking + patients row for a patient. The
// usual reason is a typo-on-creation ("MRN-0000" / "mrn-0000") that
// failed the canonical numeric-only shape, or an entry attributed to
// the wrong identifier upstream. Distinct from `update-name` because:
//
//   * It changes the *identity* the row is keyed under, not a
//     denormalized display value — every join across bookings,
//     patients, and downstream caches lands on `mrn`, so this has
//     to be atomic.
//   * Conflict potential: the target MRN might already be in use by
//     a different patient (different name / DOB). The endpoint
//     refuses the rename in that case rather than silently merging
//     two patients' histories.
//   * Required input matches the canonical Mount Sinai shape (digits
//     only — no letters, dashes, or spaces). Length is not
//     constrained because real MRNs vary; the cleanup detector
//     intentionally only flags non-numeric content, not length.
// =============================================================================

/** Canonical MRN shape — non-empty, digits only. Same predicate the
 *  Members tab's "MRN_FORMAT" cleanup detector uses, applied to the
 *  *target* MRN so we don't accept a rename into the exact bucket
 *  we're trying to leave (e.g. renaming `mrn-0000` to `MRN-0001`
 *  would just replace one non-numeric ID with another). We don't
 *  constrain length here — real Mount Sinai MRNs vary, and the
 *  earlier "6-10 digits" cap was an assumption, not a spec. */
const canonicalMrnSchema = z
  .string()
  .trim()
  .regex(
    /^[0-9]+$/u,
    "MRN must contain digits only — no letters, dashes, or spaces.",
  );

export const patientUpdateMrnRequestSchema = z.object({
  /** Current MRN of the patient being renamed. Lookups go through
   *  the same normalization (trim + lowercase) as everywhere else
   *  so casing-only drift can't break the find. */
  oldMrn: mrnSchema,
  /** Target MRN. Must be all digits, any length. */
  newMrn: canonicalMrnSchema,
});
export type PatientUpdateMrnRequest = z.infer<
  typeof patientUpdateMrnRequestSchema
>;

export const patientUpdateMrnResponseSchema = z.object({
  /** How many `bookings` rows had their MRN rewritten. */
  updatedBookings: z.number().int().nonnegative(),
  /** Whether the canonical `patients` row was also re-keyed. */
  updatedPatient: z.boolean(),
  /** The post-update canonical MRN — UI uses this to refresh the
   *  row state without re-fetching the whole Members list. */
  newMrn: z.string(),
});
export type PatientUpdateMrnResponse = z.infer<
  typeof patientUpdateMrnResponseSchema
>;

// =============================================================================
// /api/patient/update-dob
//
// Correct a patient's date of birth across every booking + the
// canonical patients row(s). Used by Admin → Members when a DOB was
// entered wrong (a common intake / TMS-import mistake). Setting one
// correct DOB also collapses a member that previously showed multiple
// conflicting DOBs ("2 DOBs" cleanup flag) into a single identity.
//
// Like update-name / update-mrn this gates edits on the caller knowing
// the right MRN (admin-only context); every change is audit-logged and,
// when TMS-origin bookings are affected, queued for TMS reconciliation
// so the Royal team fixes the same DOB upstream in TripLink.
// =============================================================================

export const patientUpdateDobRequestSchema = z.object({
  mrn: mrnSchema,
  /** New date of birth, ISO YYYY-MM-DD. Must be a real past date —
   *  the API re-validates, the dialog validates before submit. */
  dob: isoDateSchema,
});
export type PatientUpdateDobRequest = z.infer<
  typeof patientUpdateDobRequestSchema
>;

export const patientUpdateDobResponseSchema = z.object({
  /** How many `bookings` rows had their DOB rewritten. */
  updatedBookings: z.number().int().nonnegative(),
  /** Whether a canonical `patients` row was updated (false when the
   *  patient only exists in synced bookings — no patients row). */
  updatedPatient: z.boolean(),
});
export type PatientUpdateDobResponse = z.infer<
  typeof patientUpdateDobResponseSchema
>;

// =============================================================================
// /api/patient/update-phone
//
// Correct a patient's contact phone across every booking + the canonical
// patients row. Added 2026-09-02 after Terrence Forrester asked how to clean
// up bookings showing "Invalid phone" in Members review and the answer was
// that there was no way at all: the portal had no phone editor, and the
// TripLink sync only writes phone columns when they are EMPTY, so a bad value
// never self-healed.
//
// `normalizeUsPhone` now guards the ingest path and migration 0026 stripped
// the country-code cases already stored. This endpoint covers the remainder —
// the values normalization deliberately refuses to guess at (appended
// extensions, two numbers run together), which need a human who knows the
// right number.
//
// Same shape and guarantees as update-name / update-mrn / update-dob: gated on
// the caller knowing the MRN, audit-logged, and queued for TMS reconciliation
// when TMS-origin bookings are affected so Royal fixes it upstream too.
// =============================================================================

export const patientUpdatePhoneRequestSchema = z.object({
  mrn: mrnSchema,
  /** New contact phone. `usPhoneSchema` strips punctuation and enforces
   *  exactly 10 digits, so this endpoint can never store a value that
   *  would re-trip the very flag it exists to clear. */
  phone: usPhoneSchema,
  /** The value being CORRECTED, verbatim as it was shown to the admin.
   *
   *  Sent so the server can replace that specific wrong number wherever it
   *  appears for this MRN, instead of only repairing malformed rows. Terrence
   *  Forrester named the need on 2026-09-02: "sometimes we have patients that
   *  changes number or provide their caregiver number instead of theirs" —
   *  a transposed or superseded number still has ten digits, so the
   *  malformed-only predicate never matched it and the correction had nowhere
   *  to land for the many members with no `patients` row (that table is keyed
   *  on (mrn, dob) NOT NULL, so one cannot be created for a DOB-less
   *  TMS-synced member).
   *
   *  Matching on the exact prior value is what keeps this from becoming the
   *  blanket overwrite an earlier review rejected: a booking carrying a
   *  legitimately DIFFERENT number — the daughter's cell on one trip — does
   *  not match and is left alone.
   *
   *  Optional. Omitted or empty means "repair malformed rows only". */
  previousPhone: z.string().optional(),
});
export type PatientUpdatePhoneRequest = z.infer<
  typeof patientUpdatePhoneRequestSchema
>;

export const patientUpdatePhoneResponseSchema = z.object({
  /** How many `bookings` rows had their contact phone rewritten. */
  updatedBookings: z.number().int().nonnegative(),
  /** Whether a canonical `patients` row was updated (false when the
   *  patient only exists in synced bookings — no patients row). */
  updatedPatient: z.boolean(),
});
export type PatientUpdatePhoneResponse = z.infer<
  typeof patientUpdatePhoneResponseSchema
>;

// =============================================================================
// /api/patient/merge
//
// Combines two member profiles when they're the same person under two
// MRNs (typo on intake, cleanup leaving an orphan, etc.). Moves every
// `bookings` row from `fromMrn` to `toMrn`, deletes the orphan
// `patients` row, and queues a TMS reconciliation item when any moved
// rows came from TripLink.
//
// Distinct from `/update-mrn` — the latter refuses to overwrite an
// existing MRN. `/merge` is the explicit "I confirm these are the
// same person, combine them" action.
// =============================================================================

export const patientMergeRequestSchema = z.object({
  /** MRN of the member being merged away. Will be empty of any
   *  bookings + patient row by the end of the call. */
  fromMrn: mrnSchema,
  /** MRN of the surviving member. Must already exist (have at least
   *  one booking) — otherwise the caller should be using
   *  `/update-mrn` instead. */
  toMrn: mrnSchema,
});
export type PatientMergeRequest = z.infer<typeof patientMergeRequestSchema>;

export const patientMergeResponseSchema = z.object({
  bookingsMoved: z.number().int().nonnegative(),
  patientsDeleted: z.number().int().nonnegative(),
  toMrn: z.string(),
});
export type PatientMergeResponse = z.infer<typeof patientMergeResponseSchema>;

// =============================================================================
// /api/patient/delete-by-mrn
//
// Hard-deletes every `bookings` row + the `patients` row for an MRN.
// Used for expired / deceased patients and for typo'd MRNs that can't
// be merged anywhere. Audit-logged with full counts; queues a TMS
// reconciliation item when any deleted bookings came from TripLink.
// =============================================================================

export const patientDeleteByMrnRequestSchema = z.object({
  /** Allow an EMPTY string here, unlike `mrnSchema` (which requires
   *  ≥ 1 char). The Members tab surfaces an "unidentified bookings"
   *  bucket — every booking whose MRN was never captured upstream
   *  collapses into a single mrn = '' group. Deleting that bucket is
   *  a legitimate cleanup action (clears orphan records that belong to
   *  patients who already have their own profiles), so the delete
   *  endpoint has to accept the empty key. Every other caller passes a
   *  real MRN. Still capped at 50 chars; just no minimum length. */
  mrn: z.string().max(50).transform((v) => v.trim()),
});
export type PatientDeleteByMrnRequest = z.infer<
  typeof patientDeleteByMrnRequestSchema
>;

export const patientDeleteByMrnResponseSchema = z.object({
  bookingsDeleted: z.number().int().nonnegative(),
  patientsDeleted: z.number().int().nonnegative(),
});
export type PatientDeleteByMrnResponse = z.infer<
  typeof patientDeleteByMrnResponseSchema
>;
